/**
 * DustSwapStatusIndicator Component
 *
 * Small badge displayed on token cards to indicate the token's dust swap status.
 * Shows "Ready for dust swapping" for swappable tokens.
 * Shows faded "Below minimum" indicator for tokens with routes but below threshold.
 */

import { RefreshCw, AlertCircle } from 'lucide-react';
import { DUST_SWAP_CONSTANTS } from '@/lib/constants';

interface DustSwapStatusIndicatorProps {
  /** Whether the token is eligible for dust swapping (has route AND meets minimum) */
  isSwappable: boolean;
  /** Whether Jupiter found a swap route for this token */
  hasSwapRoute?: boolean;
  /** Whether the token meets the minimum value threshold */
  meetsMinimum?: boolean;
  /** Estimated SOL value (for tooltip) */
  estimatedSolValue?: number;
  /** Optional: compact mode for smaller displays */
  compact?: boolean;
}

export function DustSwapStatusIndicator({
  isSwappable,
  hasSwapRoute = false,
  meetsMinimum = false,
  estimatedSolValue,
  compact = false,
}: DustSwapStatusIndicatorProps) {
  const minValue = DUST_SWAP_CONSTANTS.MIN_SWAP_VALUE_SOL;

  // Token is fully swappable (has route AND meets minimum)
  if (isSwappable) {
    if (compact) {
      // Compact mode: just an icon
      return (
        <div
          className="absolute top-1 right-1 z-[5] flex h-5 w-5 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(74, 222, 128, 0.9)' }}
          title="Ready for dust swapping"
        >
          <RefreshCw className="h-3 w-3 text-white" />
        </div>
      );
    }

    // Full badge with text
    return (
      <div
        className="absolute bottom-1 left-1 right-1 z-[5] flex items-center justify-center gap-1 rounded px-1.5 py-0.5"
        style={{
          backgroundColor: 'rgba(74, 222, 128, 0.9)',
        }}
      >
        <RefreshCw className="h-3 w-3 text-white" />
        <span
          className="text-[10px] font-medium text-white"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Ready for dust swapping
        </span>
      </div>
    );
  }

  // Token has route but below minimum - show faded indicator
  if (hasSwapRoute && !meetsMinimum) {
    const valueText = estimatedSolValue !== undefined
      ? `Current value: ${estimatedSolValue.toFixed(4)} Credits.`
      : '';
    const tooltipText = `${valueText} Need ${minValue} Credits minimum to swap.`;

    if (compact) {
      // Compact mode: faded icon with warning
      return (
        <div
          className="absolute top-1 right-1 z-[5] flex h-5 w-5 items-center justify-center rounded-full"
          style={{ backgroundColor: 'rgba(156, 163, 175, 0.7)' }} // Gray/faded
          title={tooltipText}
        >
          <AlertCircle className="h-3 w-3 text-white" />
        </div>
      );
    }

    // Full badge with text
    return (
      <div
        className="absolute bottom-1 left-1 right-1 z-[5] flex items-center justify-center gap-1 rounded px-1.5 py-0.5"
        style={{
          backgroundColor: 'rgba(156, 163, 175, 0.7)', // Gray/faded
        }}
        title={tooltipText}
      >
        <AlertCircle className="h-3 w-3 text-white" />
        <span
          className="text-[10px] font-medium text-white"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Below minimum
        </span>
      </div>
    );
  }

  // No swap route or not applicable
  return null;
}
